import { useMemo } from 'react';
import { BadgeDollarSign, CreditCard, DollarSign, Gem } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { MapleMarketApp } from '@/data/mapleMarketApps';

interface RevenuePanelProps {
  products: MapleMarketApp[];
}

function formatMoney(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
}

export default function RevenuePanel({ products }: RevenuePanelProps) {
  const paidProducts = useMemo(
    () => products.filter((product) => !product.isFree && product.priceCents > 0),
    [products]
  );

  const freeProducts = products.length - paidProducts.length;

  const totalCatalogValue = useMemo(
    () => paidProducts.reduce((total, product) => total + product.priceCents / 100, 0),
    [paidProducts]
  );

  const averagePaidPrice = useMemo(() => {
    if (paidProducts.length === 0) {
      return 0;
    }

    return totalCatalogValue / paidProducts.length;
  }, [paidProducts.length, totalCatalogValue]);

  const highestPriced = useMemo(
    () => [...paidProducts].sort((left, right) => right.priceCents - left.priceCents).slice(0, 5),
    [paidProducts]
  );

  const priceBands = useMemo(() => {
    const bands = [
      { label: 'Free', count: products.filter((product) => product.isFree || product.priceCents === 0).length },
      { label: 'Under $5', count: products.filter((product) => product.priceCents > 0 && product.priceCents < 500).length },
      { label: '$5 to $19.99', count: products.filter((product) => product.priceCents >= 500 && product.priceCents < 2000).length },
      { label: '$20+', count: products.filter((product) => product.priceCents >= 2000).length },
    ];

    return bands;
  }, [products]);

  const statCards = [
    {
      title: 'Paid apps',
      value: paidProducts.length,
      description: 'Products currently monetized in your catalog',
      icon: DollarSign,
    },
    {
      title: 'Free apps',
      value: freeProducts,
      description: 'Products available at no charge',
      icon: Gem,
    },
    {
      title: 'Catalog list value',
      value: formatMoney(totalCatalogValue),
      description: 'Combined list price across visible paid products',
      icon: BadgeDollarSign,
    },
    {
      title: 'Average paid price',
      value: formatMoney(averagePaidPrice),
      description: 'Average price of monetized products',
      icon: CreditCard,
    },
  ];

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Revenue</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Revenue attribution is not yet tracked per marketplace product, so this panel focuses on live pricing and monetization configuration.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <div>
                <CardDescription>{card.title}</CardDescription>
                <CardTitle className="mt-2 text-3xl">{card.value}</CardTitle>
              </div>
              <div className="rounded-xl bg-primary/10 p-2 text-primary">
                <card.icon className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{card.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top priced products</CardTitle>
            <CardDescription>Your highest priced visible products.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {highestPriced.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                No paid products are configured yet.
              </div>
            ) : (
              highestPriced.map((product) => (
                <div key={product.id} className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <img src={product.image} alt={product.name} className="h-11 w-11 rounded-lg object-cover" />
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">{product.name}</div>
                      <div className="text-sm text-muted-foreground">{product.category}</div>
                    </div>
                  </div>
                  <Badge>{formatMoney(product.priceCents / 100)}</Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pricing mix</CardTitle>
            <CardDescription>How your visible products are distributed by price band.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {priceBands.map((band) => {
              const share = products.length === 0 ? 0 : Math.round((band.count / products.length) * 100);
              return (
                <div key={band.label} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground">{band.label}</span>
                    <span className="text-muted-foreground">{band.count} · {share}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted">
                    <div className="h-2 rounded-full bg-primary" style={{ width: `${share}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
